import {
    type ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState
} from "react";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentCommercialPlanMessageId } from "../../slices/chatSlice";
import { ViewContext, type ViewContextType } from "./viewContext";

export type ViewContextValue
    = | string
        | number
        | boolean
        | null
        | undefined
        | ViewContextValue[]
        | { [key: string]: ViewContextValue };

export type ViewContextData = Record<string, ViewContextValue>;

type ViewContextProviderProps = {
    children: ReactNode;
};

export const ViewContextProvider = ({
    children
}: ViewContextProviderProps): ReactNode => {
    const selectedPlanMessageId = useAppSelector(selectCurrentCommercialPlanMessageId);
    const viewContextRef = useRef<ViewContextData>({});
    const [viewContext, setViewContext] = useState<ViewContextData>({});

    const setViewContextValue = useCallback(
        (key: string, value: ViewContextValue): void => {
            const updatedViewContext: ViewContextData = {
                ...viewContextRef.current,
                [key]: value
            };

            viewContextRef.current = updatedViewContext;
            setViewContext(updatedViewContext);
        },
        []
    );

    const getViewContext = useCallback((): ViewContextData | null => {
        const combinedViewContext: ViewContextData = {
            ...viewContextRef.current,
            ...(selectedPlanMessageId == null
                ? {}
                : { selected_plan_message_id: selectedPlanMessageId })
        };

        const entries = Object.entries(combinedViewContext)
            .filter(
                ([, value]): boolean =>
                    !Array.isArray(value) || value.length > 0
            );

        if (entries.length === 0) {
            return null;
        }

        return Object.fromEntries(
            entries.map(
                ([key, value]): [string, ViewContextValue] => [
                    key,
                    value === undefined ? null : value
                ]
            )
        );
    }, [selectedPlanMessageId]);

    const value = useMemo<ViewContextType>(
        () => ({
            viewContext,
            setViewContextValue,
            getViewContext
        }),
        [viewContext, setViewContextValue, getViewContext]
    );

    return (
        <ViewContext.Provider value={value}>
            {children}
        </ViewContext.Provider>
    );
};
